import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  useRouterState,
  useNavigate,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { supabase } from "@/integrations/supabase/client";
import { Toaster } from "sonner";

function NotFoundComponent() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const navigate = useNavigate();

  useEffect(() => {
    if (pathname !== "/index") return;
    supabase.auth.getSession().then(({ data }) => {
      navigate({ to: data.session ? "/dashboard" : "/", replace: true });
    });
  }, [navigate, pathname]);

  if (pathname === "/index") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <div className="max-w-md text-center">
          <h1 className="text-xl font-semibold text-foreground">Laster siden</h1>
          <p className="mt-2 text-sm text-muted-foreground">Sender deg videre til riktig side.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-6xl font-semibold text-foreground">404</h1>
        <p className="mt-3 text-sm text-muted-foreground">Siden finnes ikke.</p>
        <Link to="/" className="btn btn-primary mt-6 inline-flex">Til forsiden</Link>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  // Logg alltid full feil til konsollen – også i produksjon – slik at feil kan diagnostiseres.
  console.error("[VERQO] Uhåndtert feil i rot-error-boundary:", error);
  console.error("[VERQO] name:", error?.name, "message:", error?.message);
  console.error("[VERQO] stack:", error?.stack ?? "(ingen stack)");
  const showDiagnostics = import.meta.env.DEV;
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);


  const reloadApplication = () => {
    if (typeof window === "undefined") {
      reset();
      return;
    }

    window.location.reload();
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-3xl text-center">
        <h1 className="text-xl font-semibold text-foreground">Noe gikk galt</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Prøv å laste siden på nytt.
        </p>
        {showDiagnostics && (
          <details className="mt-5 rounded-md border border-border bg-muted p-4 text-left" open>
            <summary className="cursor-pointer text-sm font-semibold text-foreground">
              Teknisk feilinformasjon
            </summary>
            <p className="mt-3 break-words font-mono text-xs font-semibold text-destructive">
              {error.name}: {error.message}
            </p>
            <pre className="mt-3 max-h-80 overflow-auto whitespace-pre-wrap break-words font-mono text-xs text-muted-foreground">
              {error.stack ?? "Ingen stack trace tilgjengelig."}
            </pre>
          </details>
        )}
        <button
          onClick={reloadApplication}
          className="btn btn-primary mt-6"
        >
          Prøv igjen
        </button>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "VERQO – Én plattform for håndverksbedriften" },
      { name: "description", content: "VERQO samler kunder, prosjekter, kalkyler, tilbud og dokumentasjon i én moderne plattform for håndverksbedrifter." },
      { name: "theme-color", content: "#0D0D0F" },
      { name: "msapplication-TileColor", content: "#0D0D0F" },
      { property: "og:title", content: "VERQO – Én plattform for håndverksbedriften" },
      { property: "og:description", content: "VERQO samler kunder, prosjekter, kalkyler, tilbud og dokumentasjon i én moderne plattform for håndverksbedrifter." },
      { property: "og:site_name", content: "VERQO" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:title", content: "VERQO – Én plattform for håndverksbedriften" },
      { name: "twitter:description", content: "VERQO samler kunder, prosjekter, kalkyler, tilbud og dokumentasjon i én moderne plattform for håndverksbedrifter." },
      { property: "og:image", content: "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/98c74957-c773-4219-819e-4a56ffb19453/id-preview-0d1c192a--f7b0049a-604b-4310-83a1-b58bfd5bd64c.lovable.app-1783685578590.png" },
      { name: "twitter:image", content: "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/98c74957-c773-4219-819e-4a56ffb19453/id-preview-0d1c192a--f7b0049a-604b-4310-83a1-b58bfd5bd64c.lovable.app-1783685578590.png" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "icon", type: "image/png", href: "/__l5e/assets-v1/ea7d5248-8045-4bbd-978d-60413377ec38/verqo-icon.png" },
      { rel: "apple-touch-icon", href: "/__l5e/assets-v1/ea7d5248-8045-4bbd-978d-60413377ec38/verqo-icon.png" },
      { rel: "manifest", href: "/site.webmanifest" },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      { rel: "stylesheet", href: "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap" },
    ],
  }),

  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="nb">
      <head><HeadContent /></head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const router = useRouter();

  useEffect(() => {
    const { data } = supabase.auth.onAuthStateChange((event) => {
      if (event !== "SIGNED_IN" && event !== "SIGNED_OUT" && event !== "USER_UPDATED") return;
      router.invalidate();
      if (event !== "SIGNED_OUT") queryClient.invalidateQueries();
    });
    return () => data.subscription.unsubscribe();
  }, [router, queryClient]);

  return (
    <QueryClientProvider client={queryClient}>
      <Outlet />
      <Toaster position="top-center" richColors closeButton />
    </QueryClientProvider>
  );
}
